import { ReactTFC } from "../../../utils/fp/missing-ts-parts";
import { css } from "@emotion/react";
import React, { useCallback, useEffect, useRef, useState } from "react";
import { FONTS } from "../../../styles/fonts";
import { containerStyles, overflowStyles } from "./GuidedSellingStep";
import cn from "classnames";
import { CSS_COLORS } from "../../../styles/styles-css-variables";
import { ExpandedRichText } from "@swo/shared";
import { SORichTextRenderer } from "../../_shared-components/so-rich-text";
import { CSS_MQ } from "../../../styles/breakpoints";
import useResizeObserver from "use-resize-observer";
import { UpArrowSvg } from "../../../assets";
import { type GuidedSellingTranslation } from "@swo/storefront";
export const StepHint: ReactTFC<{
  text: string | ExpandedRichText | undefined;
  translations: GuidedSellingTranslation;
}> = props => {
  const [isExpanded, setExpanded] = useState(false);
  const [canBeExpanded, setCanBeExpanded] = useState(false);

  const wrapperContainerRef = useRef<HTMLDivElement>(null);
  const textContainerRef = useRef<HTMLDivElement>(null);

  const checkCb = useCallback(() => {
    const { current: currentTextElement } = textContainerRef;
    const { current: currentWrapperElement } = wrapperContainerRef;

    if (currentTextElement && currentWrapperElement) {
      if (window.innerWidth >= 992 && window.innerWidth < 1400 && window.innerHeight < 768) {
        setCanBeExpanded(true);
      } else {
        setCanBeExpanded(currentTextElement.clientHeight - currentWrapperElement.clientHeight > 7);
      }
    }
  }, []);

  useResizeObserver<HTMLDivElement>({
    //@ts-expect-error
    ref: textContainerRef,
    onResize: checkCb,
  });

  useEffect(() => {
    setExpanded(false);

    checkCb();
  }, [props.text]);

  const text =
    typeof props.text === "string" ? (
      <p>{props.text}</p>
    ) : typeof props.text === "undefined" ? null : (
      <SORichTextRenderer richText={props.text} options={"default"} />
    );

  return (
    <div
      className={cn({ "is-visible": !!props.text })}
      css={css`
        ${containerStyles};

        ${CSS_MQ.lowResNotebook} {
          position: absolute;
          bottom: 0;
          height: 60px;
        }

        position: relative;
        --h: 26px;

        filter: opacity(0.9);
        width: 100%;

        &:not(.is-visible) {
          visibility: hidden;
        }
      `}
    >
      <div
        className={cn(isExpanded ? "expanded" : "folded", { expandable: canBeExpanded })}
        onClick={e => {
          if (canBeExpanded && !isExpanded) {
            // hack to improve clickability
            setExpanded(s => true);
          }
        }}
        css={css`
          background: ${CSS_COLORS.GreenDark};
          border-radius: 10px;

          ${FONTS.SwarovskiFutura300};
          font-size: 16px;
          color: ${CSS_COLORS.White};
          letter-spacing: 1px;
          line-height: 21px;

          --mb: 12px;
          ${CSS_MQ.atLeastMd} {
            --mb: 16px;
          }
          --pv: 12px;

          margin-bottom: var(--mb);
          padding: var(--pv) 16px;

          --icon-size: 20px;

          position: relative;
          height: calc(100% - var(--mb));

          display: grid;
          grid-template-rows: auto 18px;

          ${CSS_MQ.atLeastMd} {
            grid-row-gap: 12px;
            grid-template-rows: auto 22px;
          }

          &:not(.expandable) {
            grid-template-rows: 1fr;
          }

          &.expanded.expandable {
            position: absolute;
            height: auto;
            bottom: 0;
            width: 100%;
          }
        `}
      >
        <div
          ref={wrapperContainerRef}
          className={"text-aa"}
          css={css`
            font-size: 16px;
            ${CSS_MQ.atLeastMd} {
              font-size: 20px;
            }

            p {
              color: inherit;
              margin: 0;
              line-height: 1.2;
            }

            overflow: hidden;

            position: relative;
            --h: 50%;

            ${CSS_MQ.lowResNotebook} {
              .folded & {
                display: none !important;
              }
            }

            .folded.expandable & {
              &:before {
                content: "";
                position: absolute;
                left: 0;
                bottom: 0;
                width: 100%;
                height: var(--h);
                z-index: 1;
                background: linear-gradient(to bottom, ${CSS_COLORS.GreenDarkA01}, ${CSS_COLORS.GreenDark});

                pointer-events: none;
              }
            }
          `}
        >
          <div
            ref={textContainerRef}
            className={"text-container"}
            css={css`
              * {
                line-height: 1.3 !important;
              }
            `}
          >
            {text}
          </div>
        </div>

        <button
          disabled={!canBeExpanded}
          onClick={() => {
            setExpanded(s => !s);
          }}
          css={css`
            align-items: center;
            margin-bottom: 6px;
            min-height: 20px;

            &:not(.expandable) {
              display: none;
            }

            ${CSS_MQ.lowResNotebook} {
              & {
                display: block !important;
              }
            }
          `}
          className={cn({ expandable: canBeExpanded })}
        >
          <span
            key={typeof props.text === "string" ? props.text : JSON.stringify(props.text?.json)}
            css={css`
              display: grid;
              grid-template-columns: var(--icon-size) auto;

              grid-gap: 10px;
              font-weight: 500;
            `}
          >
            <UpArrowSvg
              css={css`
                width: var(--icon-size);
                height: var(--icon-size);
                fill: currentColor;
                padding-bottom: 2px;

                .expanded & {
                  transform-origin: center;
                  transform: rotate(180deg);
                }

                g {
                  stroke-width: 5 !important;
                }
              `}
            />
            <span
              className={"u-uppercase"}
              css={css`
                line-height: 1;
                justify-self: flex-start;
                align-self: flex-end;
              `}
            >
              {isExpanded ? props.translations.lessInfo.value : props.translations.moreInfo.value}
            </span>
          </span>
        </button>
      </div>
    </div>
  );
};
